import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShieldCheck, FileCheck2, Landmark } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // If already signed in, take them straight to the app
  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  const features = [
    { icon: Sparkles, title: "AI MoU Extraction", body: "Drop a Scope of Work — activities and milestones are pulled out and pinned to the project." },
    { icon: Landmark, title: "Budgets", body: "Live utilisation tracking, overrun guards and Excel import per project head." },
    { icon: ShieldCheck, title: "SLA-Aware Approvals", body: "Bulk approve, rebuttal trails, per-department SLAs and overdue nag emails." },
    { icon: FileCheck2, title: "GFR 12-A UC", body: "Reconciliation, period-freeze and one-click Utilization Certificate PDF." },
  ];

  return (
    <div className="min-h-screen bg-[#0F172A] text-white overflow-x-hidden">
      {/* Header */}
      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white overflow-hidden flex items-center justify-center">
            <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" data-testid="landing-logo" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight">My RKF</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Governance · v1.3</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="landing-signin-link">Sign in</Link>
          <Link to="/register">
            <Button size="sm" variant="secondary" className="bg-white text-[#0F172A] hover:bg-slate-100" data-testid="landing-register-btn">
              Create account
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="grid-bg absolute inset-0 opacity-[0.08]" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800/50 px-3 py-1 text-xs font-mono uppercase tracking-widest text-blue-300 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            Compliance-first ERP for grant-funded orgs
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl">
            From MoU to
            <br />
            Utilization Certificate<span className="text-blue-400">.</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">
            My RKF turns Scopes of Work into structured activities, tracks every rupee across the budget, and prints a GFR&nbsp;12-A certificate on demand.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/login">
              <Button size="lg" className="bg-blue-500 hover:bg-blue-400 text-white shadow-lg shadow-blue-500/20" data-testid="landing-cta-signin">
                Sign in <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/register" className="text-sm text-slate-300 hover:text-white transition-colors">
              or create a new account →
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative max-w-6xl mx-auto px-6 pb-24">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 backdrop-blur hover:border-slate-700 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-300">
                <f.icon className="w-5 h-5" />
              </div>
              <div className="font-display font-bold mt-3 text-base">{f.title}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs font-mono text-slate-500">
          <div>© {new Date().getFullYear()} RKF · My RKF v1.3 · Compliance-first</div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-slate-300 transition-colors">Sign in</Link>
            <Link to="/register" className="hover:text-slate-300 transition-colors">Create account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
